import { ExternalLink, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { SiTiktok } from "react-icons/si";
import { useSocialFields, type SocialConfig } from "../../lib/hooks/useSocialFields";
import { normalizeSocialUrl, type SocialField } from "../../lib/utils/clientSocial";
import { InlineTextRow } from "../shared/property-rows";

/** Icona del singolo social, nello stesso formato delle altre righe del pannello (h-3 w-3). */
export function socialIcon(field: SocialField) {
  const cls = "h-3 w-3";
  switch (field) {
    case "instagram_url": return <Instagram className={cls} strokeWidth={1.5} />;
    case "facebook_url": return <Facebook className={cls} strokeWidth={1.5} />;
    case "linkedin_url": return <Linkedin className={cls} strokeWidth={1.5} />;
    case "youtube_url": return <Youtube className={cls} strokeWidth={1.5} />;
    case "tiktok_url": return <SiTiktok className={cls} />;
  }
}
export function SocialFields({
  values,
  onChange,
  errors = {},
}: {
  values: any;
  onChange: (values: any) => void;
  errors?: Record<string, string>;
}) {
  const { fields } = useSocialFields();
  return (
    <div className="space-y-3">
      {fields
        .filter((f) => f.is_visible)
        .map((f) => (
          <label key={f.field_key} className="block text-sm">
            {f.field_label}
            {f.is_required && " *"}
            <input
              aria-label={f.field_label}
              type="text"
              inputMode="url"
              placeholder="https://"
              className="mt-1 w-full rounded border p-2 bg-transparent"
              value={values[f.field_key] || ""}
              onChange={(e) => onChange({ [f.field_key]: e.target.value })}
            />
            {errors[f.field_key] && (
              <span className="text-red-500">{errors[f.field_key]}</span>
            )}
          </label>
        ))}
    </div>
  );
}
/** Riga singola di un social nel pannello lead/cliente (lettura inline, modifica inline, link "Apri"). */
export function SocialDetailRow({
  field,
  client,
  canEdit,
  onSave,
}: {
  field: SocialConfig;
  client: any;
  canEdit: boolean;
  onSave: (updates: any) => Promise<boolean>;
}) {
  const url: string | undefined = client[field.field_key];
  return (
    <InlineTextRow
      icon={socialIcon(field.field_key)}
      label={field.field_label}
      value={url}
      canEdit={canEdit}
      isLink
      onSave={async (value) => {
        if (field.is_required && !value?.trim()) return false;
        try {
          return await onSave({ [field.field_key]: normalizeSocialUrl(value) });
        } catch {
          return false;
        }
      }}
      actions={
        url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title="Apri"
            onClick={(e) => e.stopPropagation()}
            className="rounded p-0.5 text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
          >
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
          </a>
        ) : undefined
      }
    />
  );
}

export function SocialDetailRows({
  client,
  canEdit,
  onSave,
}: {
  client: any;
  canEdit: boolean;
  onSave: (updates: any) => Promise<boolean>;
}) {
  const { fields } = useSocialFields();
  return (
    <>
      {fields
        .filter((f) => f.is_visible)
        .map((f) => (
          <SocialDetailRow key={f.field_key} field={f} client={client} canEdit={canEdit} onSave={onSave} />
        ))}
    </>
  );
}
